import React from 'react';
import clsx from 'clsx';
import { GlobalStyles, styled } from '@mui/material';
import FuseScrollbars from '@fuse/core/FuseScrollbars';

import FusePageSimpleHeader from './FusePageSimpleHeader';
import FusePageSimpleSidebar from './FusePageSimpleSidebar';

interface FusePageSimpleProps {
	className?: string;
	header?: React.ReactNode;
	content?: React.ReactNode;
	scroll?: 'normal' | 'page' | 'content';
	leftSidebarOpen?: boolean;
	leftSidebarWidth?: number;
	leftSidebarContent?: React.ReactNode;
	leftSidebarVariant?: string;
	leftSidebarOnClose?: () => void;
	rightSidebarOpen?: boolean;
	rightSidebarWidth?: number;
	rightSidebarContent?: React.ReactNode;
	rightSidebarVariant?: string;
	rightSidebarOnClose?: () => void;
}

type RootProps = Pick<FusePageSimpleProps, 'scroll' | 'leftSidebarWidth' | 'rightSidebarWidth'>;

const Root = styled('div')<RootProps>(({ leftSidebarWidth, rightSidebarWidth, scroll, theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	minHeight: '100%',
	position: 'relative',
	flex: '1 1 auto',
	width: '100%',
	height: 'auto',
	backgroundColor: theme.palette.background.default,

	'&.FusePageSimple-scroll-content': {
		height: '100%'
	},

	'& .FusePageSimple-wrapper': {
		display: 'flex',
		flexDirection: 'row',
		flex: '1 1 auto',
		zIndex: 2,
		minWidth: 0,
		height: '100%',
		backgroundColor: theme.palette.background.default,

		...(scroll === 'content' && {
			position: 'absolute',
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
			overflow: 'hidden'
		})
	},

	'& .FusePageSimple-header': {
		display: 'flex',
		flex: '0 0 auto',
		backgroundSize: 'cover'
	},

	'& .FusePageSimple-topBg': {
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		height: headerHeight,
		pointerEvents: 'none'
	},

	'& .FusePageSimple-contentWrapper': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		flex: '1 1 auto',
		overflow: 'hidden',
		//    WebkitOverflowScrolling: 'touch',
		zIndex: 9999
	},

	'& .FusePageSimple-toolbar': {
		height: toolbarHeight,
		minHeight: toolbarHeight,
		display: 'flex',
		alignItems: 'center'
	},

	'& .FusePageSimple-content': {
		display: 'flex',
		flex: '1 1 auto',
		alignItems: 'start',
		minHeight: 0,
		overflowY: 'auto'
	},

	'& .FusePageSimple-sidebarWrapper': {
		overflow: 'hidden',
		backgroundColor: 'transparent',
		position: 'absolute',
		'&.permanent': {
			[theme.breakpoints.up('lg')]: {
				position: 'relative',
				marginLeft: 0,
				marginRight: 0,
				transition: theme.transitions.create('margin', {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen
				}),
				'&.closed': {
					transition: theme.transitions.create('margin', {
						easing: theme.transitions.easing.easeOut,
						duration: theme.transitions.duration.enteringScreen
					}),

					'&.FusePageSimple-leftSidebar': {
						marginLeft: leftSidebarWidth ? -leftSidebarWidth : undefined
					},
					'&.FusePageSimple-rightSidebar': {
						marginRight: rightSidebarWidth ? -rightSidebarWidth : undefined
					}
				}
			}
		}
	},

	'& .FusePageSimple-sidebar': {
		position: 'absolute',
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,

		'&.permanent': {
			[theme.breakpoints.up('lg')]: {
				position: 'relative'
			}
		},
		maxWidth: '100%',
		height: '100%'
	},

	'& .FusePageSimple-leftSidebar': {
		width: leftSidebarWidth,

		[theme.breakpoints.up('lg')]: {
			borderRight: `1px solid ${theme.palette.divider}`,
			borderLeft: 0
		}
	},

	'& .FusePageSimple-rightSidebar': {
		width: rightSidebarWidth,

		[theme.breakpoints.up('lg')]: {
			borderLeft: `1px solid ${theme.palette.divider}`,
			borderRight: 0
		}
	},

	'& .FusePageSimple-sidebarHeader': {
		height: headerHeight,
		minHeight: headerHeight,
		backgroundColor: theme.palette.primary.dark,
		color: theme.palette.primary.contrastText
	},

	'& .FusePageSimple-sidebarHeaderInnerSidebar': {
		backgroundColor: 'transparent',
		color: 'inherit',
		height: 'auto',
		minHeight: 'auto'
	},

	'& .FusePageSimple-sidebarContent': {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '100%'
	},

	'& .FusePageSimple-backdrop': {
		position: 'absolute'
	}
}));

const headerHeight = 120;
const toolbarHeight = 64;

const FusePageSimple = React.forwardRef(
	(
		{
			className,
			header,
			content,
			scroll = 'page',
			leftSidebarOpen = false,
			leftSidebarWidth = 240,
			leftSidebarContent,
			leftSidebarVariant,
			leftSidebarOnClose,
			rightSidebarOpen = false,
			rightSidebarWidth = 240,
			rightSidebarContent,
			rightSidebarVariant,
			rightSidebarOnClose
		}: FusePageSimpleProps,
		ref
	) => {
		const leftSidebarRef = React.createRef<any>();
		const rightSidebarRef = React.createRef<any>();

		React.useImperativeHandle(ref, () => ({
			toggleLeftSidebar: val => {
				leftSidebarRef.current?.toggleSidebar(val);
			},
			toggleRightSidebar: val => {
				rightSidebarRef.current?.toggleSidebar(val);
			}
		}));

		return (
			<>
				<GlobalStyles
					styles={theme => ({
						...(scroll !== 'page' && {
							'#fuse-toolbar': {
								position: 'static'
							},
							'#fuse-footer': {
								position: 'static'
							}
						}),
						...(scroll === 'page' && {
							'#fuse-toolbar': {
								position: 'sticky',
								top: 0
							},
							'#fuse-footer': {
								position: 'sticky',
								bottom: 0
							}
						})
					})}
				/>
				<Root
					className={clsx('FusePageSimple-root', `FusePageSimple-scroll-${scroll}`, className)}
					scroll={scroll}
					leftSidebarWidth={leftSidebarWidth}
					rightSidebarWidth={rightSidebarWidth}
				>
					<div className="z-10 flex h-full flex-auto flex-col">
						<div className="FusePageSimple-wrapper">
							{leftSidebarContent && (
								<FusePageSimpleSidebar
									ref={leftSidebarRef}
									open={leftSidebarOpen}
									onClose={leftSidebarOnClose}
									position="left"
									sidebarWidth={leftSidebarWidth}
									variant={leftSidebarVariant || 'permanent'}
									content={leftSidebarContent}
								/>
							)}

							<div
								className="FusePageSimple-contentWrapper"
								// enable={props.scroll === 'page'}
							>
								{header && <FusePageSimpleHeader header={header} />}
								{content && (
									<FuseScrollbars
										className={clsx('FusePageSimple-content container')}
										enable={scroll === 'content'}
									>
										{content}
									</FuseScrollbars>
								)}
							</div>
							{rightSidebarContent && (
								<FusePageSimpleSidebar
									ref={rightSidebarRef}
									open={rightSidebarOpen}
									onClose={rightSidebarOnClose}
									position="right"
									sidebarWidth={rightSidebarWidth}
									variant={rightSidebarVariant || 'permanent'}
									content={rightSidebarContent}
									innerScroll
								/>
							)}
						</div>
					</div>
				</Root>
			</>
		);
	}
);

export default React.memo(styled(FusePageSimple)``);
